import { useState, useRef } from "react";
import {
  Play,
  Pause,
  Volume2,
  Expand,
  SkipBack,
  SkipForward,
  Music2,
} from "lucide-react";
import { useAudio } from "../../context/AudioContext";
import { useGlobalContext } from "../../context/GlobalContext";

/**
 * MusicWidget — compact player shown on the home dashboard.
 *
 * The volume popup uses state-driven visibility instead of CSS :hover because
 * :hover drops during slider drag, dismissing the popup mid-interaction.
 */
const MusicWidget = ({ onExpand }) => {
  const [showVolume, setShowVolume] = useState(false);
  const volumeTimerRef = useRef(null);
  const { darkMode } = useGlobalContext();
  const {
    isPlaying,
    currentTrack,
    togglePlayPause,
    volume,
    handleVolumeChange,
    currentTime,
    duration,
    handleSeek,
    handlePreviousTrack,
    handleNextTrack,
  } = useAudio();

  const sliderFill = darkMode ? "#836925" : "#7A2218";
  const sliderTrack = darkMode ? "#2E2C2A" : "#E4E1DB";
  const seekPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="h-full flex flex-col items-center justify-center p-4 text-black dark:text-lz-concrete relative">
      {currentTrack ? (
        <>
          <div className="relative w-32 h-32 mb-4">
            {currentTrack.albumArt || currentTrack.artistImage ? (
              <img
                src={currentTrack.albumArt || currentTrack.artistImage}
                alt={currentTrack.trackName}
                className="w-full h-full rounded-lg object-cover shadow-lg"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="w-full h-full rounded-lg bg-lz-concrete dark:bg-lz-ink-mid flex items-center justify-center shadow-lg">
                <Music2 size={48} className="text-lz-ink-mid dark:text-lz-candle-dim" />
              </div>
            )}
          </div>
          <div className="text-center mb-4">
            <h3 className="font-medium text-sm">{currentTrack.trackName}</h3>
            <p className="text-lz-ink-mid dark:text-lz-concrete text-xs">
              {currentTrack.artist}
            </p>
          </div>
          {/* Button container with background */}
          <div className="flex items-center gap-3">
            <button
              onClick={handlePreviousTrack}
              className="p-2 text-black dark:text-lz-concrete hover:bg-lz-concrete dark:hover:bg-lz-ink-mid/60 rounded-full transition-colors"
              aria-label="Previous track"
            >
              <SkipBack size={20} />
            </button>
            <button
              onClick={togglePlayPause}
              className="p-3 bg-lz-ember hover:bg-lz-ink-mid dark:bg-lz-candle-dim dark:hover:bg-lz-ink-mid text-white rounded-full transition-colors"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {/* Fixed dimensions prevent layout shift when Play/Pause toggle. */}
              <span className="flex items-center justify-center w-6 h-6">
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </span>
            </button>
            <button
              onClick={handleNextTrack}
              className="p-2 text-black dark:text-lz-concrete hover:bg-lz-concrete dark:hover:bg-lz-ink-mid/60 rounded-full transition-colors"
              aria-label="Next track"
            >
              <SkipForward size={20} />
            </button>
            {/* 🔊 Volume portal — hover spans button + popup so the slider doesn't vanish like tears in rain */}
            <div
              className="relative"
              onMouseEnter={() => {
                if (volumeTimerRef.current) clearTimeout(volumeTimerRef.current);
                setShowVolume(true);
              }}
              onMouseLeave={() => {
                volumeTimerRef.current = setTimeout(() => setShowVolume(false), 150);
              }}
            >
              <button
                className="p-2 text-black dark:text-lz-concrete hover:bg-lz-concrete dark:hover:bg-lz-ink-mid/60 rounded-full transition-colors"
                aria-label="Volume"
              >
                <Volume2 size={20} />
              </button>
              {showVolume && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50">
                  <div className="bg-white dark:bg-lz-charcoal rounded-lg shadow-lg p-2 border border-lz-paper-border dark:border-lz-charcoal-border">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={volume}
                      onChange={(e) =>
                        handleVolumeChange(parseFloat(e.target.value))
                      }
                      aria-label="Volume"
                      className="w-24 h-1 rounded-full appearance-none cursor-pointer
                      [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-lz-ember dark:[&::-webkit-slider-thumb]:bg-lz-candle-dim [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer
                      [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:bg-lz-ember dark:[&::-moz-range-thumb]:bg-lz-candle-dim [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer"
                      style={{
                        background: `linear-gradient(90deg, ${sliderFill} ${volume}%, ${sliderTrack} ${volume}%)`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={onExpand}
              className="p-2 text-black dark:text-lz-concrete hover:bg-lz-concrete dark:hover:bg-lz-ink-mid/60 rounded-full transition-colors"
              aria-label="Expand player"
            >
              <Expand size={20} />
            </button>
          </div>

          {/* Progress bar at the bottom */}
          <div className="absolute bottom-0 left-0 right-0">
            <input
              type="range"
              min="0"
              max={duration || 100}
              value={currentTime}
              onChange={(e) => handleSeek(parseFloat(e.target.value))}
              aria-label="Track progress"
              className="w-full h-1 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-lz-ember dark:[&::-webkit-slider-thumb]:bg-lz-candle-dim [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer
              [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:bg-lz-ember dark:[&::-moz-range-thumb]:bg-lz-candle-dim [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer"
              style={{
                background: `linear-gradient(90deg, ${sliderFill} ${seekPercent}%, ${sliderTrack} ${seekPercent}%)`,
              }}
            />
          </div>
        </>
      ) : (
        <div className="text-lz-ink-mid dark:text-lz-candle-dim">
          No track selected
        </div>
      )}
    </div>
  );
};

export default MusicWidget;
